/**
 * REQUIRED-DOCUMENT ENFORCEMENT — shared logic
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * One implementation of "which documents must be uploaded before this
 * application may be submitted", used by:
 *
 *   - the server submit endpoints (`POST /api/applications/submit` and the
 *     admin submit-on-behalf endpoint in server.ts) — the hard gate;
 *   - the applicant UI (ApplicantFlow) — badges, descriptions, progress and
 *     the submit-time explanation that mirror the gate;
 *   - the workflow snapshot (server/workflows/snapshot.ts) — so the
 *     pre-vetting "Documents approved" check counts the same required set.
 *
 * The configuration source is the program's `requiredDocuments`
 * (ProgramDocument[] from src/lib/programConfig.ts): a document blocks
 * submission iff `required === true` and it is not archived. Legacy shapes
 * are normalised by `normaliseDocuments` — a legacy `string[]` list becomes
 * all-required (identical to the historical behaviour, where every listed
 * document was presented as required), and a program with NO configured
 * documents has an empty required set and never blocks.
 *
 * Upload identity: applicant uploads live in the
 * `applications/{id}/documents` subcollection and carry
 * `type === <configured document label>` (see ApplicantFlow
 * uploadDocumentFile). An upload counts as "uploaded" when a non-superseded
 * record exists for the label whose status is not 'Rejected' (a rejected
 * document must be re-uploaded; a superseded record is an old version).
 */

import { normaliseDocuments, type ProgramDocument } from './programConfig';

/**
 * Document labels removed from requirements business-wide. ApplicantFlow has
 * always filtered these out of the applicant checklist even when a stored
 * program still lists them; the server MUST apply the same exclusion or it
 * would block submission on documents the applicant can never see or upload.
 */
export const REMOVED_DOCUMENT_TYPES = new Set<string>([
  'DS-2019 Form',
  'DS-2019 Form (issued by sponsor)',
  'Training Plan (DS-7002)',
  'Internship Offer Letter / Training Plan (DS-7002)',
  'SEVIS Fee Receipt',
  'Employer Invitation / Sponsorship Letter',
  'Visa Application Form (DS-160)',
  'Work Experience Letters (min 1 year typically required)',
  'Work Experience Letters',
]);

export interface RequiredDocumentEntry {
  id: string;
  label: string;
  description?: string;
}

/** Minimal shape of an uploaded-document record (subcollection doc data). */
export interface UploadedDocumentLike {
  type?: unknown;
  status?: unknown;
  superseded?: unknown;
}

function labelKey(label: string): string {
  return label.trim().toLowerCase();
}

/**
 * Parse the application-level extras stored in `applications.documentRequired`.
 *
 * The field's ONLY legitimate content is extra labels an admin attached to
 * one application (writers join with '\n'). Two historical poisons must
 * never resurrect phantom requirements:
 *
 *  1. Program labels were once stamped into this field at application
 *     creation, comma-joined ("Passport, Resume / CV, …"). Program config
 *     is authoritative for its own documents — the caller drops any extra
 *     that names a configured document (see buildRequiredDocumentSet).
 *  2. A configured label may itself CONTAIN commas (e.g. "Health Insurance
 *     Proof (DOS minimums: $100,000 medical, …)"). Naive comma-splitting
 *     shredded it into bogus slots ("$100", "000 medical", …) — 18 cards
 *     instead of 11 in the live test. Guard: when a comma-joined legacy
 *     value is split, any piece that is a FRAGMENT of a configured label
 *     (a substring of it, or vice versa) is dropped.
 *
 * Parsing: split on newlines first; a line that exactly matches a
 * configured label stays whole; other comma-bearing lines are split with
 * the fragment guard above. Returns trimmed, de-duplicated labels in
 * first-seen order.
 *
 * RE-JOIN PASS (follow-up, 2026-10-12): the fragment guard compares pieces
 * against the CURRENT configured labels. When a label is later RENAMED in
 * program config (the live W&T "Health Insurance Proof (DOS minimums:
 * $100,000 medical, …)" became just "Health Insurance Proof", the minimums
 * moving into the description), the stored poisoned string's tail pieces
 * ("$25", "000 medical", "deductible ≤$500)") no longer substring-match
 * ANY configured label and survived as phantom extras on pre-fix
 * applications (17 cards, "0 of 10 required"). So comma-bearing lines now
 * go through reconstruction BEFORE the fragment guard:
 *   1. Greedy re-join — consecutive pieces re-joined with ", " that
 *      exactly equal a configured label are consumed as that label
 *      (program config owns it; it is not an extra).
 *   2. Anchored expansion runs — a piece that STARTS with a configured
 *      label followed by an opening "(" with unbalanced parens begins an
 *      expanded form of that label (e.g. the old long Health Insurance
 *      label under the new short config): consume pieces until the
 *      parens balance.
 *   3. Leftover pieces fall through to the original fragment guard.
 */
export function parseApplicationDocumentExtras(
  value: string | string[] | null | undefined,
  configuredLabels: string[] = [],
): string[] {
  const configuredKeys = new Set(configuredLabels.map(labelKey));
  const isFragmentOfConfigured = (piece: string): boolean => {
    const key = labelKey(piece);
    if (!key) return true;
    for (const label of configuredLabels) {
      const lk = labelKey(label);
      if (lk === key) continue; // exact match — handled by the caller, not a fragment
      if (lk.includes(key) || key.includes(lk)) return true;
    }
    return false;
  };

  const out: string[] = [];
  const seen = new Set<string>();
  const push = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const key = labelKey(trimmed);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(trimmed);
  };

  const lines: string[] = Array.isArray(value)
    ? value.flatMap((v) => String(v ?? '').split('\n'))
    : String(value || '').split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Without configured labels there is no safe way to comma-split (a
    // label may itself contain commas) — newline-only parsing keeps such
    // labels whole.
    if (!trimmed.includes(',') || configuredKeys.has(labelKey(trimmed)) || configuredLabels.length === 0) {
      push(trimmed);
      continue;
    }
    // Legacy comma-joined value: reconstruct before judging pieces.
    const pieces = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    const consumed = new Array<boolean>(pieces.length).fill(false);

    // Pass 1 — greedy re-join: the longest run of consecutive pieces that
    // re-joins (with ", ") to EXACTLY a configured label is that label —
    // consumed by program config, never an extra.
    for (let i = 0; i < pieces.length; i++) {
      if (consumed[i]) continue;
      for (let j = pieces.length - 1; j >= i; j--) {
        const joined = pieces.slice(i, j + 1).join(', ');
        if (configuredKeys.has(labelKey(joined))) {
          for (let k = i; k <= j; k++) consumed[k] = true;
          break;
        }
      }
    }

    // Pass 2 — anchored expansion runs: a piece that begins with a
    // configured label and then opens an unbalanced "(" is an EXPANDED
    // form of that label (its parenthetical detail was later moved into
    // the description, shortening the configured label). Consume pieces
    // until the parentheses balance.
    const parenDelta = (s: string): number =>
      (s.match(/\(/g) || []).length - (s.match(/\)/g) || []).length;
    for (let i = 0; i < pieces.length; i++) {
      if (consumed[i]) continue;
      const pieceKey = labelKey(pieces[i]);
      const anchored = configuredLabels.some((label) => {
        const lk = labelKey(label);
        return pieceKey.length > lk.length && pieceKey.startsWith(lk) && /^\s*\(/.test(pieceKey.slice(lk.length));
      });
      if (!anchored) continue;
      let balance = 0;
      for (let j = i; j < pieces.length; j++) {
        balance += parenDelta(pieces[j]);
        consumed[j] = true;
        if (balance <= 0) break;
      }
    }

    // Pass 3 — leftovers: the original fragment guard, then genuine extras.
    for (let i = 0; i < pieces.length; i++) {
      if (consumed[i]) continue;
      if (isFragmentOfConfigured(pieces[i])) continue;
      push(pieces[i]);
    }
  }
  return out;
}

/**
 * Builds the enforced required set for an application:
 *   program-configured documents with `required === true` (not archived,
 *   minus REMOVED_DOCUMENT_TYPES)
 * + application-level admin requirements (`applications.documentRequired`,
 *   a legacy free-text list of extra labels an admin attached to this one
 *   application — the applicant UI already renders these as required slots,
 *   so the server enforces them too).
 *
 * Returns entries in configuration order, de-duplicated by label.
 */
export function buildRequiredDocumentSet(
  programRequiredDocuments: unknown,
  applicationDocumentRequired?: string | string[] | null
): RequiredDocumentEntry[] {
  const entries: RequiredDocumentEntry[] = [];
  const seen = new Set<string>();

  const configured = normaliseDocuments(programRequiredDocuments) as ProgramDocument[];
  // EVERY configured label (required or not, archived or not) — program
  // config is authoritative for its own documents, so an application
  // extra may never re-add one (the poisoned-create bug turned every
  // optional program document into a submission blocker).
  const configuredKeys = new Set(configured.map((doc) => labelKey(doc.label)));

  for (const doc of configured) {
    if (!doc.required || doc.archived) continue;
    if (REMOVED_DOCUMENT_TYPES.has(doc.label)) continue;
    const key = labelKey(doc.label);
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push({ id: doc.id, label: doc.label, description: doc.description || undefined });
  }

  const extraLabels = parseApplicationDocumentExtras(
    applicationDocumentRequired,
    configured.map((doc) => doc.label),
  );
  for (const label of extraLabels) {
    if (REMOVED_DOCUMENT_TYPES.has(label)) continue;
    const key = labelKey(label);
    if (seen.has(key)) continue;
    if (configuredKeys.has(key)) continue; // program config owns this label
    seen.add(key);
    entries.push({ id: `app_${key.replace(/[^a-z0-9]+/g, '-')}`, label });
  }

  return entries;
}

/**
 * One configured document source for fragment resolution (Item 7 below):
 * a label string, or any object carrying a label/name and an optional
 * description — the normalised ProgramDocument shape qualifies.
 */
export type ConfiguredDocumentSource =
  | string
  | { label?: unknown; name?: unknown; description?: unknown };

/**
 * #1125 Item 7 — resolve a stored upload TYPE that is a FRAGMENT of a
 * configured document's display text back to that document's label.
 *
 * #1124 rejoined the fragment SLOTS on the requirement side, but uploads
 * stored BEFORE the fix keep fragment types of their own: when the
 * Health Insurance document was the single long label
 * "Health Insurance Proof (DOS minimums: $100,000 medical, $25,000
 * repatriation, $50,000 medical evacuation, deductible ≤$500)", the old
 * client split that string on commas, so uploads carry types like
 * '000 medical', '$25', '000 repatriation', '$50',
 * '000 medical evacuation' and 'deductible ≤$500)'. The configured
 * label is now "Health Insurance Proof" with the minimums in its
 * DESCRIPTION, so the resolver tests each configured document's
 * expanded forms — the bare label and `label (description)` — split on
 * commas. A stored type equal to a configured label returns that label
 * unchanged (clean uploads are untouched); a fragment maps only when it
 * belongs to EXACTLY ONE configured document; ambiguous pieces return
 * null and callers keep the stored type. Read-time only — the stored
 * data is never rewritten.
 */
export function resolveUploadedDocumentType(
  type: unknown,
  configuredDocs: readonly ConfiguredDocumentSource[] | null | undefined,
): string | null {
  const stored = String(type ?? '').trim();
  if (!stored) return null;
  const docs = (configuredDocs || [])
    .map((doc) => {
      if (typeof doc === 'string') return { label: doc.trim(), description: '' };
      if (!doc || typeof doc !== 'object') return null;
      return {
        label: String(doc.label ?? doc.name ?? '').trim(),
        description: String(doc.description ?? '').trim(),
      };
    })
    .filter((d): d is { label: string; description: string } => !!d && !!d.label);
  if (docs.length === 0) return null;
  const storedKey = labelKey(stored);
  for (const doc of docs) {
    if (labelKey(doc.label) === storedKey) return doc.label;
  }
  const expandedFormsOf = (doc: { label: string; description: string }) =>
    doc.description ? [doc.label, `${doc.label} (${doc.description})`] : [doc.label];
  // A12: test the FULL expanded form (label + parenthetical, commas
  // intact) BEFORE the comma-fragment splitting below — an upload stored
  // under a complete legacy long label equals no single comma piece and
  // no bare label, so without this pass it could never resolve.
  const fullMatches = new Set<string>();
  for (const doc of docs) {
    for (const form of expandedFormsOf(doc)) {
      if (labelKey(form) === storedKey) fullMatches.add(doc.label);
    }
  }
  if (fullMatches.size === 1) return Array.from(fullMatches)[0];
  if (fullMatches.size > 1) return null;
  const matches = new Set<string>();
  for (const doc of docs) {
    for (const form of expandedFormsOf(doc)) {
      for (const piece of form.split(',')) {
        if (piece.trim() && labelKey(piece) === storedKey) matches.add(doc.label);
      }
    }
  }
  return matches.size === 1 ? Array.from(matches)[0] : null;
}

/**
 * Labels of uploaded document TYPES that count as "uploaded" for a label:
 * a non-superseded record whose status is not 'Rejected'. A record with no
 * status (legacy) counts — it exists, which is what "uploaded" means.
 *
 * When `configuredDocs` are supplied, each stored type is first passed
 * through resolveUploadedDocumentType (Item 7) so pre-fix fragment types
 * count toward the document they belong to.
 */
export function uploadedDocumentTypes(
  uploaded: UploadedDocumentLike[],
  configuredDocs?: readonly ConfiguredDocumentSource[] | null,
): Set<string> {
  const types = new Set<string>();
  for (const doc of uploaded || []) {
    if (!doc || doc.superseded === true) continue;
    const status = String(doc.status || '').trim().toLowerCase();
    if (status === 'rejected') continue;
    const rawType = String(doc.type || '').trim();
    if (!rawType) continue;
    const resolved = configuredDocs
      ? resolveUploadedDocumentType(rawType, configuredDocs) || rawType
      : rawType;
    types.add(labelKey(resolved));
  }
  return types;
}

/** Required entries with no counting upload — the submit blockers. */
export function findMissingRequiredDocuments(
  required: RequiredDocumentEntry[],
  uploaded: UploadedDocumentLike[],
  configuredDocs?: readonly ConfiguredDocumentSource[] | null,
): RequiredDocumentEntry[] {
  const uploadedTypes = uploadedDocumentTypes(uploaded, configuredDocs);
  return required.filter((entry) => !uploadedTypes.has(labelKey(entry.label)));
}

/** How many of the required set have a counting upload (progress display). */
export function countUploadedRequiredDocuments(
  required: RequiredDocumentEntry[],
  uploaded: UploadedDocumentLike[],
  configuredDocs?: readonly ConfiguredDocumentSource[] | null,
): number {
  return required.length - findMissingRequiredDocuments(required, uploaded, configuredDocs).length;
}

/**
 * #1125 Item 6 — the FIRST document that is genuinely REQUIRED and
 * missing, from raw program config + application extras. The journey
 * "What To Do Now" banner uses this so it can only ever prompt a
 * required document: #1124 made uploaded docs count as satisfied, but
 * the banner still picked the first missing entry from the FULL
 * configured list (required flag ignored) and framed optional
 * documents ("Vetted Job Offer (signed)") as part of the "required
 * application documents checklist" on an already-submitted application.
 * Returns null when nothing required is missing.
 */
export function firstMissingRequiredDocument(
  programRequiredDocuments: unknown,
  applicationDocumentRequired: string | string[] | null | undefined,
  uploaded: UploadedDocumentLike[],
  configuredDocs?: readonly ConfiguredDocumentSource[] | null,
): RequiredDocumentEntry | null {
  const requiredSet = buildRequiredDocumentSet(programRequiredDocuments, applicationDocumentRequired);
  if (requiredSet.length === 0) return null;
  const missing = findMissingRequiredDocuments(requiredSet, uploaded, configuredDocs);
  return missing.length > 0 ? missing[0] : null;
}

/**
 * PR-3 §3.3 — THE document-requirements resolver: every configured
 * document for an application with its required/optional classification,
 * from the same inputs as buildRequiredDocumentSet (program config +
 * application-level extras). The draft wizard's documents step, the
 * submitted application's documents tab, and the journey banner all read
 * classification from here, so a document can never be "Optional" in one
 * surface and "Missing / Required" in another (the live bug: the optional
 * Vetted Job Offer labelled "MISSING / OPTIONAL" after submission).
 *
 * Classification contract: a document is 'required' iff it is in the
 * enforced required set (program `required === true`, or an
 * application-level extra — extras are always requirements); every other
 * configured document is 'optional'. UI labels are exactly "Required" /
 * "Optional" — never a combined "Missing / Required" style string.
 */
export interface DocumentRequirement {
  id: string;
  label: string;
  description?: string;
  classification: 'required' | 'optional';
  source: 'program' | 'application';
}

export function resolveDocumentRequirements(
  programRequiredDocuments: unknown,
  applicationDocumentRequired?: string | string[] | null
): DocumentRequirement[] {
  const entries: DocumentRequirement[] = [];
  const seen = new Set<string>();

  const configured = normaliseDocuments(programRequiredDocuments) as ProgramDocument[];
  const configuredKeys = new Set(configured.map((doc) => labelKey(doc.label)));

  for (const doc of configured) {
    if (doc.archived) continue;
    if (REMOVED_DOCUMENT_TYPES.has(doc.label)) continue;
    const key = labelKey(doc.label);
    if (seen.has(key)) continue;
    seen.add(key);
    entries.push({
      id: doc.id,
      label: doc.label,
      description: doc.description || undefined,
      classification: doc.required ? 'required' : 'optional',
      source: 'program',
    });
  }

  const extraLabels = parseApplicationDocumentExtras(
    applicationDocumentRequired,
    configured.map((doc) => doc.label),
  );
  for (const label of extraLabels) {
    if (REMOVED_DOCUMENT_TYPES.has(label)) continue;
    const key = labelKey(label);
    if (seen.has(key)) continue;
    if (configuredKeys.has(key)) continue; // program config owns this label
    seen.add(key);
    entries.push({
      id: `app_${key.replace(/[^a-z0-9]+/g, '-')}`,
      label,
      classification: 'required',
      source: 'application',
    });
  }

  return entries;
}

/** Labels of the enforced required set, in configuration order. */
export function requiredDocumentLabels(
  programRequiredDocuments: unknown,
  applicationDocumentRequired?: string | string[] | null,
): string[] {
  return buildRequiredDocumentSet(programRequiredDocuments, applicationDocumentRequired).map((e) => e.label);
}

/**
 * Session 6 item 2 — the BEST current record for each document type.
 *
 * An applicant can hold several records of one type (an upload that was
 * rejected, then a re-upload that was approved). Listing every record
 * makes the Review step claim the type is REJECTED when an approved
 * record of the same type exists (employer-journey walk, October
 * 2026). Presentation rule:
 *
 *  - one row per (resolved) type, carrying its best record;
 *  - an Approved record wins over everything; then SystemPreApproved;
 *    then any in-flight record (Pending / PendingAdminReview /
 *    FurtherReviewRequested / status-less legacy);
 *  - a Rejected record is the row ONLY when no non-rejected record of
 *    that type exists, and then it carries its rejection reason so the
 *    applicant sees WHY and can re-upload;
 *  - superseded records never win and never form their own row;
 *  - ties inside a rank go to the most recently uploaded record.
 *
 * Read-time only — stored data is never rewritten.
 */
export interface BestDocumentEntry<T extends UploadedDocumentLike = UploadedDocumentLike> {
  /** Display label for the type (the configured label when it resolves). */
  type: string;
  /** The winning record for the type. */
  doc: T;
  /** True when the winning record is Rejected and no better record exists. */
  rejectedOnly: boolean;
  /** The rejection reason for a rejected-only row ('' otherwise / when none stored). */
  rejectionReason: string;
}

function documentStatusRank(status: unknown): number {
  const s = String(status ?? '').trim().toLowerCase();
  if (s === 'approved' || s === 'approved admin') return 3;
  if (s === 'systempreapproved') return 2;
  if (s === 'rejected') return 0;
  return 1;
}

function documentRecordTime(doc: UploadedDocumentLike & Record<string, any>): string {
  const raw = doc.uploadDate ?? doc.uploadedAt ?? doc.createdAt ?? doc.updatedAt ?? '';
  return typeof raw === 'string' ? raw : '';
}

function documentRejectionReason(doc: UploadedDocumentLike & Record<string, any>): string {
  if (typeof doc.rejectionReason === 'string' && doc.rejectionReason.trim()) return doc.rejectionReason.trim();
  if (Array.isArray(doc.rejectionReasons)) {
    const joined = doc.rejectionReasons.filter((r: any) => typeof r === 'string' && r.trim()).join('; ');
    if (joined) return joined;
  }
  if (typeof doc.rejectionNote === 'string' && doc.rejectionNote.trim()) return doc.rejectionNote.trim();
  if (typeof doc.returnNote === 'string' && doc.returnNote.trim()) return doc.returnNote.trim();
  return '';
}

export function bestDocumentPerType<T extends UploadedDocumentLike & Record<string, any>>(
  uploaded: readonly T[] | null | undefined,
  configuredDocs?: readonly ConfiguredDocumentSource[] | null,
): BestDocumentEntry<T>[] {
  const groups = new Map<string, { type: string; doc: T; rank: number; time: string }>();
  for (const doc of uploaded || []) {
    if (!doc || doc.superseded === true) continue;
    const rawType = String(doc.type || '').trim();
    if (!rawType) continue;
    const resolved = configuredDocs
      ? resolveUploadedDocumentType(rawType, configuredDocs) || rawType
      : rawType;
    const key = labelKey(resolved);
    const rank = documentStatusRank(doc.status);
    const time = documentRecordTime(doc);
    const current = groups.get(key);
    if (!current || rank > current.rank || (rank === current.rank && time >= current.time)) {
      groups.set(key, { type: resolved, doc, rank, time });
    }
  }
  return Array.from(groups.values()).map(({ type, doc, rank }) => ({
    type,
    doc,
    rejectedOnly: rank === 0,
    rejectionReason: rank === 0 ? documentRejectionReason(doc) : '',
  }));
}
